/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Html Dom Event 를 확장한다.
 *
 * @file /src/utils/Events.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 29.
 */
export interface HoverEvent extends MouseEvent {
    hover: boolean;
}

export interface EventMap extends GlobalEventHandlersEventMap {
    longpress: PointerEvent;
    hover: PointerEvent;
    input: InputEvent;
    [key: string]: Event;
}

export class Events {
    /**
     * keyup 직후 이어지는 브라우저 기본 activation 을 잡기 위한 해제 지연시간.
     */
    private static readonly keyReleaseDelay = 50;

    /**
     * Enter, Escape 키 최초 keydown 상태.
     */
    private static pressedKeys: Map<
        string,
        {
            key: string;
            target: EventTarget | null;
            focusMoved: boolean;
            releaseTimer: number | null;
        }
    > = new Map();

    private static initialized = false;

    /**
     * 이벤트 관리를 초기화한다.
     *
     * Enter, Escape 키를 keypress 처럼 최초 1회만 동작하게 관리한다.
     * keyup 이전에 포커스가 이동하거나 최초 target 이 제거되면,
     * 같은 물리 키 입력으로 인한 추가 keydown 및 브라우저 기본 click 을 차단한다.
     */
    static init(): void {
        if (Events.initialized == true) {
            return;
        }
        Events.initialized = true;

        window.addEventListener('keydown', Events.captureKeydown, true);
        window.addEventListener('keyup', Events.releaseKey, true);
        window.addEventListener('blur', Events.clearPressedKeys);
        document.addEventListener('focusin', Events.captureFocusin, true);
        document.addEventListener('click', Events.captureClick, true);
        document.addEventListener('visibilitychange', Events.clearPressedKeysWhenHidden);
    }

    /**
     * keypress 처럼 최초 keydown 만 허용할 키인지 확인한다.
     *
     * @param e - 키보드 이벤트
     * @returns 처리대상 여부
     */
    private static isKeypressKey(e: KeyboardEvent): boolean {
        return e.key === 'Enter' || e.key === 'Escape';
    }

    /**
     * 같은 물리 키 입력을 추적하기 위한 키 식별자를 가져온다.
     *
     * @param e - 키보드 이벤트
     * @returns 키 식별자
     */
    private static getKeyId(e: KeyboardEvent): string {
        return e.code || e.key;
    }

    /**
     * 이벤트 대상이 최초 keydown 대상과 동일한 흐름인지 확인한다.
     *
     * @param target - 확인할 이벤트 대상
     * @param originalTarget - 최초 keydown 대상
     * @returns 동일대상 여부
     */
    private static isSameEventTarget(target: EventTarget | null, originalTarget: EventTarget | null): boolean {
        if (target === originalTarget) {
            return true;
        }

        if (target instanceof Node && originalTarget instanceof Node) {
            return originalTarget.contains(target);
        }

        return false;
    }

    /**
     * 이벤트를 차단한다.
     *
     * @param e - 이벤트
     */
    private static stopEvent(e: Event): void {
        if (e.cancelable == true) {
            e.preventDefault();
        }
        e.stopImmediatePropagation();
    }

    /**
     * 최초 keydown 이후 같은 키가 눌려있는 동안 발생하는 keydown 을 차단한다.
     *
     * @param e - 키보드 이벤트
     */
    private static captureKeydown(e: KeyboardEvent): void {
        if (Events.isKeypressKey(e) == false) {
            return;
        }

        const keyId = Events.getKeyId(e);
        const pressedKey = Events.pressedKeys.get(keyId);
        if (pressedKey !== undefined) {
            if (pressedKey.releaseTimer !== null) {
                window.clearTimeout(pressedKey.releaseTimer);
                pressedKey.releaseTimer = null;
            }

            if (Events.isSameEventTarget(e.target, pressedKey.target) == false) {
                pressedKey.focusMoved = true;
            }

            Events.stopEvent(e);
            return;
        }

        Events.pressedKeys.set(keyId, {
            key: e.key,
            target: e.target,
            focusMoved: false,
            releaseTimer: null,
        });
    }

    /**
     * keydown 중 포커스가 최초 target 밖으로 이동했는지 기록한다.
     *
     * @param e - 포커스 이벤트
     */
    private static captureFocusin(e: FocusEvent): void {
        if (Events.pressedKeys.size == 0) {
            return;
        }

        Events.pressedKeys.forEach((pressedKey) => {
            if (Events.isSameEventTarget(e.target, pressedKey.target) == false) {
                pressedKey.focusMoved = true;
            }
        });
    }

    /**
     * Enter keydown 중 target 변경으로 발생한 브라우저 기본 click 을 차단한다.
     *
     * @param e - 마우스 이벤트
     */
    private static captureClick(e: MouseEvent): void {
        if (e.isTrusted == false || e.detail !== 0) {
            return;
        }

        for (const pressedKey of Events.pressedKeys.values()) {
            if (pressedKey.key !== 'Enter') {
                continue;
            }

            const targetRemoved = pressedKey.target instanceof Node && pressedKey.target.isConnected == false;
            const targetChanged = Events.isSameEventTarget(e.target, pressedKey.target) == false;
            if (targetChanged == true && (pressedKey.focusMoved == true || targetRemoved == true)) {
                Events.stopEvent(e);
                return;
            }
        }
    }

    /**
     * keyup 이 발생한 키를 눌림 상태에서 해제한다.
     *
     * @param e - 키보드 이벤트
     */
    private static releaseKey(e: KeyboardEvent): void {
        if (Events.isKeypressKey(e) == false) {
            return;
        }

        const keyId = Events.getKeyId(e);
        const pressedKey = Events.pressedKeys.get(keyId);
        if (pressedKey === undefined) {
            return;
        }

        if (pressedKey.releaseTimer !== null) {
            window.clearTimeout(pressedKey.releaseTimer);
        }

        pressedKey.releaseTimer = window.setTimeout(() => {
            Events.pressedKeys.delete(keyId);
        }, Events.keyReleaseDelay);
    }

    /**
     * keyup 을 받지 못하는 상황에서 키 눌림 상태를 초기화한다.
     */
    private static clearPressedKeys(): void {
        Events.pressedKeys.forEach((pressedKey) => {
            if (pressedKey.releaseTimer !== null) {
                window.clearTimeout(pressedKey.releaseTimer);
            }
        });
        Events.pressedKeys.clear();
    }

    /**
     * 문서가 숨겨져 keyup 을 받지 못하는 상황에서 키 눌림 상태를 초기화한다.
     */
    private static clearPressedKeysWhenHidden(): void {
        if (document.visibilityState == 'hidden') {
            Events.clearPressedKeys();
        }
    }
}
